import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import { Label } from "design-system/atoms/Label"
import { Skeleton } from "design-system/atoms/Skeleton"
import { Textarea } from "design-system/atoms/Textarea"
import { Card } from "design-system/molecules/Card"
import { Section } from "design-system/molecules/Section"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useBulkEmailVerifier } from "../../hooks/emailVerifier/useBulkEmailVerifier"

export type EmailVerifierResult = {
  email: string
  isValid: boolean
  reason?: string
  smtpCode?: number
  smtpResponse?: string
  verificationMethod?: string
}

export default function EmailVerifier() {
  const [emailList, setEmailList] = useState("")
  const { verifyEmails, isLoading, results, error } = useBulkEmailVerifier()
  const { t } = useTranslation()
  const handleVerify = () => {
    const emails = emailList
      .split("\n")
      .map((email) => email.trim())
      .filter((email) => email.length > 0)

    if (emails.length === 0) return

    verifyEmails(emails)
  }

  const getStatusBadge = (result: EmailVerifierResult) => {
    if (result.isValid) {
      return (
        <Badge variant="success" className="text-xs">
          {`${t("backoffice.emailVerifier.results.valid")} (${result.verificationMethod || "basic"})`}
        </Badge>
      )
    }

    const reasonColors: Record<
      string,
      | "default"
      | "secondary"
      | "destructive"
      | "outline"
      | "gradient"
      | "success"
      | "testing"
      | null
      | undefined
    > = {
      smtp_recipient_rejected: "destructive",
      disposable_email: "secondary",
      invalid_format: "destructive",
      no_mx_records: "destructive",
      problematic_domain: "secondary",
    }

    return (
      <Badge variant={reasonColors[result.reason || ""] || "destructive"} className="text-xs">
        {`${t("backoffice.emailVerifier.results.invalid")} ${result.reason?.replace(/_/g, " ") || "Invalid"}`}
      </Badge>
    )
  }

  const validCount = results.filter((r) => r.isValid).length
  const invalidCount = results.length - validCount

  return (
    <div className="space-y-6">
      <Section
        title={t("backoffice.emailVerifier.title")}
        description={t("backoffice.emailVerifier.description")}
      />

      <div className="space-y-4">
        <div>
          <Label htmlFor="email-list" className="text-sm font-medium">
            {t("backoffice.emailVerifier.emailAddresses")}
          </Label>
          <p className="text-xs text-gray-500 mt-1">
            {t("backoffice.emailVerifier.emailAddressesDescription")}
          </p>
        </div>

        <Textarea
          id="email-list"
          placeholder={`user@gmail.com
test@outlook.com
fake@disposable.com
invalid-email
customer@company.com`}
          value={emailList}
          onChange={(e) => setEmailList(e.target.value)}
          rows={8}
          className="font-mono text-sm"
          disabled={isLoading}
        />

        <div className="flex items-center justify-between gap-2">
          <div className="text-sm text-gray-500">
            {`${emailList.split("\n").filter((e) => e.trim()).length} ${t("backoffice.emailVerifier.emailAddressesReadyToVerify")}`}
          </div>

          <Button onClick={handleVerify} disabled={isLoading || !emailList.trim()} className="px-6">
            {isLoading
              ? t("backoffice.emailVerifier.verifying")
              : t("backoffice.emailVerifier.verify")}
          </Button>
        </div>
      </div>

      {error && (
        <Card className="p-4 border-red-200 bg-red-50">
          <div className="text-red-800 text-sm">
            <strong>{t("backoffice.emailVerifier.error")}</strong> {error}
          </div>
        </Card>
      )}

      {(isLoading || results.length > 0) && (
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {t("backoffice.emailVerifier.results.title")}
              </h2>
              {results.length > 0 && (
                <div className="flex gap-4 text-sm">
                  <span className="text-green-600 font-medium">
                    {`✅ ${validCount} ${t("backoffice.emailVerifier.results.count.valid")}`}
                  </span>
                  <span className="text-red-600 font-medium">
                    {`❌ ${invalidCount} ${t("backoffice.emailVerifier.results.count.invalid")}`}
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              {isLoading && (
                <>
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-3 border rounded-md"
                    >
                      <Skeleton className="h-4 w-48" />
                      <Skeleton className="h-6 w-20" />
                    </div>
                  ))}
                </>
              )}

              {results.map((result, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 border rounded-md hover:bg-gray-50"
                >
                  <div className="flex-1">
                    <div className="font-mono text-sm">{result.email}</div>
                    {result.smtpResponse && (
                      <div className="text-xs text-gray-500 mt-1">
                        {t("backoffice.emailVerifier.results.smtpVerification")}{" "}
                        {result.smtpResponse}
                      </div>
                    )}
                  </div>
                  <div className="ml-4">{getStatusBadge(result)}</div>
                </div>
              ))}
            </div>

            {results.length > 0 && (
              <div className="pt-4 border-t">
                <div className="text-xs text-gray-500 space-y-1">
                  <div>
                    <strong>{t("backoffice.emailVerifier.results.smtpVerification")}</strong>{" "}
                    {t("backoffice.emailVerifier.results.smtpVerificationDescription")}
                  </div>
                  <div>
                    <strong>{t("backoffice.emailVerifier.results.mxValidation")}</strong>{" "}
                    {t("backoffice.emailVerifier.results.mxValidationDescription")}
                  </div>
                  <div>
                    <strong>{t("backoffice.emailVerifier.results.disposableDetection")}</strong>{" "}
                    {t("backoffice.emailVerifier.results.disposableDetectionDescription")}
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  )
}
